import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { CheckSquare, Upload, Camera, X, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function MyTasksCard({ tasks: initialTasks, onRefresh }) {
  const [tasks, setTasks] = useState(initialTasks || []);
  const [completionDialog, setCompletionDialog] = useState(null);
  const [completionNotes, setCompletionNotes] = useState('');

  // Sync with parent props
  useEffect(() => {
    setTasks(initialTasks || []);
  }, [initialTasks]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const fetchTasks = async () => {
      const token = localStorage.getItem('token');
      try {
        const res = await fetch(`${API_URL}/api/tasks`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setTasks(data);
        }
      } catch {}
    };
    const interval = setInterval(fetchTasks, 30000);
    return () => clearInterval(interval);
  }, []);
  const [completionAttachments, setCompletionAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleUpdateStatus = async (taskId, newStatus) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/tasks/${taskId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        toast.success(newStatus === 'in_progress' ? 'Task started!' : 'Task updated');
        setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
        onRefresh?.();
      } else {
        toast.error('Failed to update task');
      }
    } catch {
      toast.error('Connection error');
    }
  };

  const openCompletionDialog = (task) => {
    setCompletionDialog(task);
    setCompletionNotes('');
    setCompletionAttachments([]);
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);

    const newAttachments = [];
    for (const file of files) {
      const formData = new FormData();
      formData.append('files', file);
      const token = localStorage.getItem('token');
      try {
        const res = await fetch(`${API_URL}/api/upload-images`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        if (res.ok) {
          const data = await res.json();
          if (data.urls?.length > 0) {
            newAttachments.push({
              url: data.urls[0],
              name: file.name,
              type: file.type.startsWith('image') ? 'image' : file.type.startsWith('video') ? 'video' : 'document'
            });
          }
        }
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    setCompletionAttachments(prev => [...prev, ...newAttachments]);
    setUploading(false);
    if (newAttachments.length > 0) toast.success(`${newAttachments.length} file(s) uploaded`);
    e.target.value = '';
  };

  const handleComplete = async () => {
    if (!completionDialog) return;
    setSubmitting(true);
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/tasks/${completionDialog.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'completed',
          completion_notes: completionNotes || undefined,
          completion_attachments: completionAttachments.length > 0 ? completionAttachments : undefined
        })
      });
      if (response.ok) {
        toast.success('Task completed!');
        setTasks(prev => prev.map(t => t.id === completionDialog.id ? { ...t, status: 'completed', completion_notes: completionNotes, completion_attachments: completionAttachments } : t));
        setCompletionDialog(null);
        onRefresh?.();
      } else {
        toast.error('Failed to complete task');
      }
    } catch {
      toast.error('Connection error');
    }
    setSubmitting(false);
  };

  const priorityColors = {
    high: 'bg-red-100 text-red-700 border-red-200',
    medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    low: 'bg-green-100 text-green-700 border-green-200'
  };

  const statusColors = {
    pending: 'bg-gray-100 text-gray-700',
    in_progress: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700'
  };

  return (
    <>
      <Card data-testid="my-tasks-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckSquare className="w-5 h-5" />
            My Tasks ({tasks.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {tasks.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No tasks assigned</p>
          ) : (
            <div className="space-y-3">
              {tasks.map((task) => (
                <div key={task.id} className="border rounded-lg p-3 bg-white" data-testid={`my-task-${task.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1">
                      <h4 className="font-semibold text-sm">{task.title}</h4>
                      {task.description && (
                        <p className="text-xs text-gray-600 mt-1">{task.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Badge className={statusColors[task.status] || ''}>
                        {task.status === 'in_progress' ? 'In Progress' : task.status?.charAt(0).toUpperCase() + task.status?.slice(1)}
                      </Badge>
                      {task.priority && (
                        <span className={`text-xs px-1.5 py-0.5 rounded border ${priorityColors[task.priority] || ''}`}>
                          {task.priority}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Task meta */}
                  <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mb-2">
                    {task.due_date && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Due: {new Date(task.due_date).toLocaleDateString()}
                      </span>
                    )}
                    {task.created_by_name && <span>From: {task.created_by_name}</span>}
                  </div>

                  {/* Attachments from creator */}
                  {task.attachments?.length > 0 && (
                    <div className="flex gap-1 flex-wrap mb-2">
                      {task.attachments.map((att, idx) => (
                        <a key={idx} href={`${API_URL}${att.url}`} target="_blank" rel="noopener noreferrer">
                          {att.type === 'image' ? (
                            <img src={`${API_URL}${att.url}`} alt={att.name} className="w-12 h-12 object-cover rounded border hover:opacity-80" />
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 bg-gray-100 rounded text-xs border hover:bg-gray-200">{att.name || 'File'}</span>
                          )}
                        </a>
                      ))}
                    </div>
                  )}

                  {/* Completion proof */}
                  {task.completion_attachments?.length > 0 && (
                    <div className="mb-2">
                      <p className="text-xs text-green-600 font-medium mb-1">Completion proof:</p>
                      <div className="flex gap-1 flex-wrap">
                        {task.completion_attachments.map((att, idx) => (
                          <a key={idx} href={`${API_URL}${att.url}`} target="_blank" rel="noopener noreferrer">
                            <img src={`${API_URL}${att.url}`} alt="proof" className="w-12 h-12 object-cover rounded border border-green-300 hover:opacity-80" />
                          </a>
                        ))}
                      </div>
                      {task.completion_notes && (
                        <p className="text-xs text-gray-600 mt-1 italic">{task.completion_notes}</p>
                      )}
                    </div>
                  )}

                  {/* Action buttons */}
                  {task.status !== 'completed' && (
                    <div className="flex gap-2 mt-2">
                      {task.status === 'pending' && (
                        <Button size="sm" onClick={() => handleUpdateStatus(task.id, 'in_progress')} data-testid={`start-task-${task.id}`}>
                          Start Task
                        </Button>
                      )}
                      {task.status === 'in_progress' && (
                        <Button size="sm" variant="default" className="bg-green-600 hover:bg-green-700" onClick={() => openCompletionDialog(task)} data-testid={`complete-task-${task.id}`}>
                          <CheckSquare className="w-3 h-3 mr-1" /> Mark Complete
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Completion Dialog */}
      <Dialog open={!!completionDialog} onOpenChange={(open) => { if (!open) setCompletionDialog(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckSquare className="w-5 h-5 text-green-600" />
              Complete Task
            </DialogTitle>
          </DialogHeader>
          {completionDialog && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <h4 className="font-semibold text-sm">{completionDialog.title}</h4>
                {completionDialog.description && (
                  <p className="text-xs text-gray-600 mt-1">{completionDialog.description}</p>
                )}
              </div>

              <div>
                <Label>Completion Notes (optional)</Label>
                <Textarea
                  value={completionNotes}
                  onChange={(e) => setCompletionNotes(e.target.value)}
                  placeholder="Describe what was done..."
                  rows={3}
                  data-testid="completion-notes"
                />
              </div>

              <div>
                <Label>Upload Proof (photos/documents)</Label>
                <div className="border-2 border-dashed rounded-lg p-4 text-center mt-1">
                  <input
                    type="file"
                    multiple
                    accept="image/*,video/*,.pdf,.doc,.docx"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="completion-upload"
                    data-testid="completion-file-input"
                  />
                  <label htmlFor="completion-upload" className="cursor-pointer">
                    <Camera className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500">Tap to upload photos or files</p>
                    <p className="text-xs text-gray-400">Supports images, videos, PDFs</p>
                  </label>
                </div>
                {uploading && <p className="text-xs text-blue-600 mt-1">Uploading...</p>}
                {completionAttachments.length > 0 && (
                  <div className="flex gap-2 flex-wrap mt-2">
                    {completionAttachments.map((att, idx) => (
                      <div key={idx} className="relative">
                        {att.type === 'image' ? (
                          <img src={`${API_URL}${att.url}`} alt={att.name} className="w-16 h-16 object-cover rounded border" />
                        ) : (
                          <div className="w-16 h-16 bg-gray-100 rounded border flex items-center justify-center text-xs">{att.name?.slice(0, 8)}</div>
                        )}
                        <button
                          className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center text-xs"
                          onClick={() => setCompletionAttachments(prev => prev.filter((_, i) => i !== idx))}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setCompletionDialog(null)}>Cancel</Button>
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={handleComplete}
                  disabled={submitting}
                  data-testid="submit-completion-btn"
                >
                  {submitting ? 'Submitting...' : 'Complete Task'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default MyTasksCard;
